//@flow
import type {Country} from './country';
import type {ThemeColor} from './theme';

import type {OrderState} from './order';

export type TaxInvoiceTypeName = 'BOLETA' | 'FACTURA' | 'NOTA_CREDITO';

export type TaxInvoiceType = {
  country: Country,
  id: string,
  label: string,
  name: TaxInvoiceTypeName,
  type: 'tax-invoice-type',
};

export type StoreTaxInvoiceType = {
  country: Country,
  createdAt: number,
  enabled: boolean,
  id: string,
  storeId: number,
  storeTaxInvoiceProviderId: string,
  taxInvoiceType: TaxInvoiceType,
  taxInvoiceTypeId: string,
  type: 'store-tax-invoice-type',
  updatedAt: number,
};

export interface StoreTaxInvoiceAddress {
  address: string;
  admin1: number;
  admin2: number;
  admin3: number;
  country: Country;
  createdAt: number;
  id: string;
  type: string;
  updatedAt: number;
}

export interface TaxInvoiceStoreProvider {
  automaticGeneration: boolean;
  createdAt: number;
  enabled: boolean;
  id: string;
  storeId: string;
  storetaxinvoiceaddress: StoreTaxInvoiceAddress;
  storetaxinvoicetype: StoreTaxInvoiceType[];
  taxInvoiceProviderId: string;
  taxTransmitterCode: string;
  taxTransmitterName: string;
  type: string;
  updatedAt: number;
}

export type EInvoiceProvider = {
  id: string,
  name: string,
  enabled: boolean,
  logo: string,
  providerConfig?: TaxInvoiceStoreProvider,
};

export type TaxInvoiceResponse = {
  country: Country,
  id: string,
  label: string,
  name: string,
  type: 'tax-invoice-provider',
};
export type TaxInvoice = {
  id: string,
  countryId: string,
  name: string,
  label: string,
  configured?: boolean,
  enabled?: boolean,
  automaticGeneration?: boolean,
  taxTransmitterName?: string,
  taxTransmitterCode?: string,
  storeTaxInvoiceProviderId?: string,
};

export type TaxInvoiceStoreTypeRequest = {
  tax_invoice_type_id: string,
  country: string,
  enabled: boolean,
};

export type CreateTaxInvoiceProviderRequest = {
  tax_invoice_provider_id: string,
  enabled: boolean,
  automatic_generation: boolean,
  tax_transmitter_name: string,
  tax_transmitter_code: string,
  store_tax_invoice_types: TaxInvoiceStoreTypeRequest[],
  store_tax_invoice_address: {
    address: string,
    country: string,
    admin1: number,
    admin2: number,
    admin3: number,
  },
};

export type TaxInvoiceOrderRequest = {
  taxInvoiceType: string,
  taxReceiverName: string,
  taxReceiverCode: string,
  address: string,
  country?: string,
  admin1: string,
  admin2: string,
  admin3: string,
};

export type TaxInvoiceDocumentType = {
  id: string,
  label: string,
  name: string,
  code: string,
};

export type TaxInvoiceStateType =
  | 'SCHEDULED'
  | 'ACCEPTED'
  | 'REJECTED'
  | 'PENDING'
  | 'FAILED'
  | 'CANCELED';
export type TaxInvoiceStateLabelType =
  | 'En cola'
  | 'Cancelado'
  | 'En proceso'
  | 'Emitido'
  | 'Fallido'
  | 'Rechazado';

export const TAX_INVOICE_STATE_LABELS: {
  [TaxInvoiceStateType]: TaxInvoiceStateLabelType,
} = {
  SCHEDULED: 'En cola',
  ACCEPTED: 'Emitido',
  REJECTED: 'Rechazado',
  PENDING: 'En proceso',
  CANCELED: 'Cancelado',
  FAILED: 'Fallido',
};

export const TAX_INVOICE_STATE_COLORS: {
  [TaxInvoiceStateType]: ThemeColor,
} = {
  SCHEDULED: 'warning',
  ACCEPTED: 'success',
  REJECTED: 'danger',
  PENDING: 'info',
  CANCELED: 'danger',
  FAILED: 'danger',
};

export const ORDER_STATES_FOR_TAX_INVOICE: OrderState[] = [
  'PAID',
  'ON_THE_WAY',
  'DELIVERED',
];

export type TaxInvoiceDocument = {
  orderId: string,
  createdAt: number,
  orderTaxInvoiceId: string,
  clientName: string,
  state: {
    id: TaxInvoiceStateType,
    label: TaxInvoiceStateLabelType,
  },
  orderAmount: string,
  isManualOrder: boolean,
  documentType: TaxInvoiceDocumentType,
  issueDate?: number,
  canBeCancelledWithoutCreditNote: boolean,
  pdfUrl?: string,
  xmlUrl?: string,
  formattedExternalReferenceId?: string,
};

export type OrderTaxInvoiceDataRequest = {
  taxInvoiceType: string,
  taxReceiverName: string,
  taxReceiverCode: string,
  country: string,
  address: {
    address: string,
    country?: string,
    admin1: string,
    admin2: string,
    admin3: string,
  },
};

export type OrderTaxInvoiceType = {
  country: {
    code: string,
    code3: string,
    id: string,
    name: string,
    phonePrefix: string,
    type: string,
  },
  id: string,
  label: string,
  name: TaxInvoiceTypeName,
  type: string,
};

export type TaxInvoiceSetting = {
  enabled: boolean,
  taxInvoiceSetting: {
    [key: string]: boolean,
  },
};

export type TaxInvoiceSettingRequest = {
  enabled: boolean,
  taxInvoiceSetting: {
    [key: string]: boolean,
  },
};
